import { useCallback, useEffect, useState } from 'react';
import { parentApi } from '../services/parentApi';

// Known codes -> safe, specific copy. Anything else falls back to a generic
// message — never render the raw backend message directly to the parent.
// Unmapped errors don't carry a stable err.code the UI can key off of (the
// backend sanitizes those to a generic message before they leave the
// server — see the parent-assign 500 in docs/parent-assign-500-handoff.md
// for a live example), so err.message isn't something to build UI copy on
// either way; this map + fallback is the one place that copy is decided.
const FRIENDLY_MESSAGES = {
  STUDENT_NOT_FOUND: "We couldn't find this student.",
};

function friendlyMessage(e) {
  return FRIENDLY_MESSAGES[e?.code] || 'Failed to load student. Please try again.';
}

export function useStudentDetail(studentId) {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await parentApi.getStudent({ studentId });
      setStudent(data);
    } catch (e) {
      // logger.error logs unconditionally, even in production — not what we
      // want here, since the raw message can carry backend internals a
      // parent shouldn't see (even just via their own browser console).
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('[useStudentDetail] Failed to load student:', e);
      }
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  return { student, loading, error, reload: load };
}
